import { Button } from '@/components/ui/button';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  EditorIntroIcon,
  EditorExtendIcon,
  EditorFadeOutIcon,
  EditorOutroIcon,
  EditorRefillIcon,
  EditorTrimIcon,
} from '@/components/Icons';
import useEditorStore, { EditorOperatorEnum } from '@/state/editor-store';
import { EditorOperatorConfigMap } from '../../module/util';
import { isEmpty } from 'lodash-es';
import JenErrorToast from '@/components/ui/jen-error-toast';
import { EditorButton } from './EditorButton';

export const MIN_DURATION_FOR_OPERATIONS = 10;

export const DISABLED_OPERATIONS = [EditorOperatorEnum.INTRO, EditorOperatorEnum.REFILL, EditorOperatorEnum.OUTRO];

interface EditorButtonConfig {
  key: EditorOperatorEnum;
  text: string;
  span: number;
  icon: React.ReactNode;
  onClick: () => Promise<void>;
}

const WaveformButtonGroup: React.FC = () => {
  const [selectOperator, setSelectOperator] = useState<EditorOperatorEnum | undefined>(undefined);

  const { editor, operator, toggleOperator, setFinalDuration, setTrimLoading } = useEditorStore((state) => ({
    editor: state.editor,
    operator: state.operator,
    toggleOperator: state.toggleOperator,
    setFinalDuration: state.setFinalDuration,
    setTrimLoading: state.setTrimLoading,
  }));

  const duration = editor?.audio?.duration;

  useEffect(() => {
    if (operator) {
      setSelectOperator(operator);
    }
  }, [operator]);

  // Function to update the final duration
  const updateFinalDuration = (editor: any, setFinalDuration: any) => {
    const iFinalDuration = editor.calcFinalDuration();
    editor.finalDuration = iFinalDuration;
    setFinalDuration(iFinalDuration);
  };

  const isOperationDisabled = (key: EditorOperatorEnum) => {
    return duration && duration < MIN_DURATION_FOR_OPERATIONS && DISABLED_OPERATIONS.includes(key);
  };

  const handleButtonClick = (config: EditorButtonConfig) => {
    const { key, onClick } = config;

    if (isOperationDisabled(key)) {
      return toast(<JenErrorToast error={new Error('Tracks under 10 seconds cannot use this operation')} />, {
        className: '!border-0 !shadow-none',
        unstyled: true,
      });
    }

    if (key === EditorOperatorEnum.NONE) return;

    if (key === operator) {
      editor?.resetOperator();
      toggleOperator(selectOperator || key, undefined);
      return setSelectOperator(undefined);
    }

    setSelectOperator(key);
    toggleOperator(selectOperator || key, key);
    if (editor) {
      onClick();
    }
  };

  const getEditorButtons = (): EditorButtonConfig[] => [
    {
      key: EditorOperatorEnum.INTRO,
      text: EditorOperatorEnum.INTRO,
      span: 1,
      icon: <EditorIntroIcon fillColor={isOperationDisabled(EditorOperatorEnum.INTRO) ? '#4a4d4f' : ''} />,
      onClick: async () => {
        await editor?.intro();
        updateFinalDuration(editor, setFinalDuration);
      },
    },
    // { key: 'FADE_OUT', text: 'FADE OUT', span: 1 },
    {
      key: EditorOperatorEnum.TRIM,
      text: EditorOperatorEnum.TRIM,
      span: 2,
      icon: <EditorTrimIcon />,
      onClick: async () => {
        await editor?.trim(0, editor.audio.duration, setFinalDuration);
      },
    },
    {
      key: EditorOperatorEnum.REFILL,
      text: EditorOperatorEnum.REFILL,
      span: 2,
      icon: <EditorRefillIcon fillColor={isOperationDisabled(EditorOperatorEnum.REFILL) ? '#4a4d4f' : ''} />,
      onClick: async () => {
        await editor?.refill(11, 21);
        updateFinalDuration(editor, setFinalDuration);
      },
    },
    {
      key: EditorOperatorEnum.NONE,
      text: '',
      span: 4,
      icon: null,
      onClick: () => new Promise(() => {}),
    },
    {
      key: EditorOperatorEnum.EXTEND,
      text: EditorOperatorEnum.EXTEND,
      span: 2,
      icon: <EditorExtendIcon />,
      onClick: async () => {
        await editor?.extend();
        updateFinalDuration(editor, setFinalDuration);
      },
    },
    {
      key: EditorOperatorEnum.FADE_OUT,
      text: 'fade out',
      span: 1,
      icon: <EditorOutroIcon />,
      onClick: async () => {
        await editor?.fadeOut(EditorOperatorConfigMap[EditorOperatorEnum.FADE_OUT].default);
        updateFinalDuration(editor, setFinalDuration);
      },
    },
    {
      key: EditorOperatorEnum.OUTRO,
      text: EditorOperatorEnum.OUTRO,
      span: 1,
      icon: <EditorFadeOutIcon fillColor={isOperationDisabled(EditorOperatorEnum.OUTRO) ? '#4a4d4f' : ''} />,
      onClick: async () => {
        await editor?.outro();
        updateFinalDuration(editor, setFinalDuration);
      },
    },
  ];

  if (isEmpty(editor)) return null;

  return (
    <>
      <div className="w-[550px] bg-neutral-900 px-10 py-2">
        <div className="flex items-center gap-4 w-full">
          <div className="flex items-center flex-1">
            <div className="w-1.5 h-1.5 rounded-full border border-neutral-600"></div>
            <div className="h-[1px] bg-neutral-600 w-full"></div>
          </div>

          <span className="text-[9px] text-neutral-600 whitespace-nowrap px-2" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            CLICK &amp; DRAG TO SELECT PART OF TRACK
          </span>

          <div className="flex items-center flex-1">
            <div className="h-[1px] bg-neutral-600 w-full"></div>
            <div className="w-1.5 h-1.5 rounded-full border border-neutral-600"></div>
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        {getEditorButtons().map((buttonConfig) => (
          <EditorButton
            key={buttonConfig.key}
            config={buttonConfig}
            isSelected={selectOperator === buttonConfig.key}
            duration={duration}
            minDuration={MIN_DURATION_FOR_OPERATIONS}
            disabledOperations={DISABLED_OPERATIONS}
            onClick={() => handleButtonClick(buttonConfig)}
          />
        ))}
      </div>
    </>
  );
};

export default WaveformButtonGroup;
